import React, { useState, useRef, useEffect } from 'react';

const SUGGESTED_PROMPTS = [
  'What ingredients should I avoid in shampoo?',
  'Find me a clean fragrance-free moisturizer',
  'Is sodium lauryl sulfate harmful?',
  'What makes a dish soap truly clean?',
];

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
          <span className="text-caption font-semibold text-primary-600">CS</span>
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-body leading-relaxed ${
          isUser
            ? 'bg-primary-500 text-white rounded-tr-sm'
            : 'bg-surface-card border border-neutral-200 text-neutral-800 rounded-tl-sm shadow-sm'
        }`}
      >
        {message.content.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < message.content.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mr-3 mt-1">
        <span className="text-caption font-semibold text-primary-600">CS</span>
      </div>
      <div className="bg-surface-card border border-neutral-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1 items-center h-5">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-neutral-300"
              style={{ animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatPage() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your CleanShopper assistant. Ask me about ingredients, brands, or what to avoid — I'll search our product database and give you straight answers.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function sendMessage(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setError(null);

    // Build API payload — exclude the initial assistant greeting from history
    // so the API only receives real conversation turns
    const apiMessages = updatedMessages
      .slice(1) // skip the welcome message
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? 'Request failed');
      }

      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const showSuggestions = messages.length === 1;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-8 pb-4 border-b border-neutral-200 bg-surface-card flex-shrink-0">
        <h1 className="text-h2 text-neutral-900">Ask CleanShopper</h1>
        <p className="text-small text-neutral-500 mt-1">
          Ingredient questions, product recommendations, safety ratings.
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-4">
        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {loading && <TypingIndicator />}

        {error && (
          <div className="text-center">
            <p className="text-caption text-error">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-caption text-neutral-400 hover:text-neutral-600 mt-1 transition-colors duration-fast"
            >
              Dismiss
            </button>
          </div>
        )}

        {showSuggestions && !loading && (
          <div className="flex flex-col gap-2 mt-2">
            <p className="text-caption text-neutral-400 text-center">Try asking…</p>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTED_PROMPTS.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => sendMessage(prompt)}
                  className="text-left px-4 py-3 rounded-lg border border-neutral-200 bg-surface-card text-small text-neutral-700 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-fast"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-200 bg-surface-card">
        <div className="flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about an ingredient, brand, or product…"
            rows={1}
            disabled={loading}
            className="flex-1 resize-none bg-neutral-100 border border-neutral-200 rounded-xl px-4 py-3 text-body text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:bg-surface-card transition-colors duration-fast disabled:opacity-50"
            style={{ minHeight: '48px', maxHeight: '120px' }}
            onInput={e => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            type="button"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent-500 text-white flex items-center justify-center hover:bg-accent-400 active:bg-accent-600 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <p className="text-caption text-neutral-400 mt-2">
          Press Enter to send · Shift+Enter for new line
        </p>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

export default ChatPage;
